/* Gleva · Direction B · gvb-sections.js — behaviour for the PDP content sections (BUILD-SPEC §3.1, §5):
   gvb-howto (tabs + face map), gvb-types, gvb-box, gvb-shades, gvb-shade-guide, gvb-tones, gvb-cross-sell,
   gvb-reviews. Ported from final-b/_src/pdp.js and the lander comps' page scripts.
   ES module (`import { G } from 'gvb'`), loaded by each of those sections as type="module" (the browser runs it once).
   Every section root carries data-gvb-sec="<name>"; init is idempotent and re-runs on shopify:section:load.
   Buy-box contract (see _build/pdp-sections/NOTES.md):
   - listens to Impact's `variant:change` (only from the page product's main form) to follow the buy box;
   - selects a variant in the buy box with `gvb:select-variant` {productId, variantId} (cancelable: a PDP module that
     owns the buy box can preventDefault and do it itself), then Impact's own option inputs, then gvb tier rows;
   - selects a Kaching tier with `gvb:pick-tier` {title} (cancelable), then the matching .kaching-bundles__bar;
   - follows `gvb:tier-change` {price} for the free-delivery note;
   - fires `gvb:tone-change` {tone, pans} (gvb-tones → the face map) and keeps the last one on G.tone. */
import { G } from 'gvb';

const { $, $$ } = G;
const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
const smooth = () => (G.reduce ? 'auto' : 'smooth');
const store = () => G.store || {};
const threshold = () => Number(store().free_threshold) || 89000;

/* ---------- tabs: click + arrows + roving tabindex (how-to, hair types, shades) ---------- */
function tabs(list, onPick) {
  const ts = $$('[role="tab"]', list);
  const pick = (t, focus) => {
    ts.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', on ? 'true' : 'false'); x.tabIndex = on ? 0 : -1; });
    onPick(t);
    if (focus) t.focus();
  };
  ts.forEach((t, k) => {
    t.addEventListener('click', () => pick(t));
    t.addEventListener('keydown', (e) => {
      const n = ts.length;
      let j = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (k + 1) % n;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (k - 1 + n) % n;
      if (e.key === 'Home') j = 0;
      if (e.key === 'End') j = n - 1;
      if (j !== null) { e.preventDefault(); pick(ts[j], true); }
    });
  });
  return pick;
}

/* ---------- the page product's buy box ---------- */
function mainFormId(pid) {
  const pr = document.querySelector('product-rerender[id^="product-info-' + pid + '-"][observe-form]');
  if (pr) return pr.getAttribute('observe-form');
  const f = document.querySelector('form[id^="product-form-' + pid + '-"]');
  return f ? f.id : null;
}

/* select a variant in the buy box; optionIds = Impact option value ids (data-option-ids) */
function selectVariant(root, vid, optionIds) {
  const pid = root.dataset.productId;
  const ev = new CustomEvent('gvb:select-variant', { bubbles: true, cancelable: true, detail: { productId: Number(pid), variantId: Number(vid), source: root } });
  if (!document.dispatchEvent(ev)) return true;
  const fid = mainFormId(pid);
  if (fid) {
    const ids = String(optionIds || '').split(',').filter(Boolean);
    const inputs = ids.map((oid) => document.querySelector('input[data-option-position][form="' + fid + '"][value="' + oid + '"]')).filter(Boolean);
    if (inputs.length && inputs.length === ids.length) {
      const todo = inputs.filter((i) => !i.checked);
      todo.forEach((i) => { i.checked = true; });
      if (todo.length) todo[todo.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
  }
  /* gvb tier rows bound to a variant (snippets/gvb-tier-row.liquid) */
  const row = document.querySelector('label.tier[data-variant-id="' + vid + '"] input, input[data-variant-id="' + vid + '"]');
  if (row) { if (!row.checked) row.click(); return true; }
  return false;
}

/* a <form>'s .id is its control named "id" (the variant input), so read the attribute */
const formIdOf = (el) => (el && el.getAttribute ? el.getAttribute('id') : null);

/* follow the buy box: Impact fires variant:change on the product form (bubbles) */
function onVariant(root, cb) {
  const ids = String(root.dataset.variantIds || '').split(',');
  const pid = root.dataset.productId;
  const h = (e) => {
    const v = e.detail && e.detail.variant;
    if (!v || ids.indexOf(String(v.id)) < 0) return;
    const fid = mainFormId(pid);
    if (fid && formIdOf(e.target) && formIdOf(e.target) !== fid) return;
    cb(v);
  };
  document.addEventListener('variant:change', h);
  return h;
}

function buyBoxTarget(pid) {
  const fid = pid ? mainFormId(pid) : null;
  return $('.kaching-bundles__bars') || $('[data-tiers]') || $('#tiers')
    || (fid && document.querySelector('variant-picker[form-id="' + fid + '"]'))
    || (pid && document.querySelector('product-rerender[id^="product-info-' + pid + '-"]')) || null;
}
function toBuyBox(pid) {
  const t = buyBoxTarget(pid);
  if (t) t.scrollIntoView({ behavior: smooth(), block: 'center' });
}

/* select a Kaching tier (or a gvb tier row) by its title, then bring the tiers into view */
function pickTier(title) {
  const ev = new CustomEvent('gvb:pick-tier', { bubbles: true, cancelable: true, detail: { title } });
  if (document.dispatchEvent(ev)) {
    const key = (t) => norm(t).replace(/[^a-z0-9₹]+/g, ' ').trim();
    const want = key(title);
    const bars = $$('.kaching-bundles__bar');
    const titleOf = (b) => key(($('.kaching-bundles__bar-title', b) || b).textContent);
    const bar = bars.find((b) => titleOf(b) === want) || bars.find((b) => titleOf(b).indexOf(want) >= 0);
    if (bar) {
      const input = $('input[type="radio"]', bar);
      (input || $('.kaching-bundles__bar-main', bar) || bar).click();
    } else {
      const row = $$('label.tier').find((l) => key(l.dataset.name || ($('.tier-name', l) || l).textContent) === want);
      if (row) { const i = $('input', row); if (i && !i.checked) i.click(); }
    }
  }
  const t = buyBoxTarget();
  if (t) {
    t.scrollIntoView({ behavior: smooth(), block: 'center' });
    setTimeout(() => {
      const f = $('.kaching-bundles__bar--selected input, label.tier.is-on input', t.closest('section, .shopify-section') || document);
      if (f) f.focus({ preventScroll: true });
    }, 450);
  }
}

/* add one variant with /cart/add.js (G.cartAdd fires variant:add → gvb:added + toast, and cart:change), then
   refresh Impact's cart (drawer, count) in case the add response came back without its bundled sections */
async function addVariant(btn, id, meta) {
  if (!id || btn.disabled) return;
  btn.setAttribute('aria-busy', 'true');
  try {
    await G.cartAdd([{ id: Number(id), quantity: 1 }], meta);
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    G.flashAdded(btn);
  } catch (e) {
    /* Impact shows the error banner on cart:error (fired by G.cartAdd) */
  } finally {
    btn.removeAttribute('aria-busy');
  }
}

/* swap an <img> once the new file has loaded (no flash of an empty frame) */
function swapImg(im, src, srcset) {
  if (!im || !src || im.getAttribute('src') === src) return;
  im.style.opacity = 0;
  const pre = new Image();
  pre.onload = () => {
    if (srcset) im.srcset = srcset; else im.removeAttribute('srcset');
    im.src = src;
    im.style.opacity = 1;
  };
  pre.onerror = pre.onload;
  if (srcset) { pre.sizes = im.sizes; pre.srcset = srcset; }
  pre.src = src;
}

/* ======================= sections ======================= */
const inits = {};

/* ---------- gvb-howto: tabs, or the face map (pan picker drives zones + step text) ---------- */
inits.howto = (root) => {
  const seg = $('.howto-seg', root);
  if (seg) tabs(seg, (t) => { $$('.howto-panel', root).forEach((p) => { p.hidden = p.id !== t.getAttribute('aria-controls'); }); });

  const pp = $('.panpick', root), fm = $('.facemap', root), cap = $('[data-fm-cap]', root);
  if (pp && fm) {
    let tone = root.dataset.tone || '';
    const caption = () => {
      const on = $('[role="tab"][aria-selected="true"]', pp);
      if (cap && on) cap.textContent = $('.pp-name', on).textContent + (tone ? ' · ' + tone : '');
    };
    tabs(pp, (t) => {
      fm.setAttribute('data-zone', t.dataset.zone);
      $$('.howto-texts .howto-panel', root).forEach((p) => { p.hidden = p.id !== t.getAttribute('aria-controls'); });
      caption();
    });
    const paint = (d) => {
      if (!d) return;
      tone = d.tone || tone;
      $$('.pan[data-pan]', root).forEach((p) => {
        const k = p.dataset.pan, src = d.pans && d.pans[k];
        if (!src) return;
        if (src.color) p.style.setProperty('--pc', src.color);
        const im = $('img', p);
        if (im && src.src) swapImg(im, src.src, src.srcset);
      });
      Object.keys(d.pans || {}).forEach((k) => { if (d.pans[k].color) fm.style.setProperty('--c-' + k, d.pans[k].color); });
      caption();
    };
    document.addEventListener('gvb:tone-change', (e) => paint(e.detail));
    if (G.tone) paint(G.tone);
  }
  const tier = $('[data-pick-tier]', root);
  if (tier) tier.addEventListener('click', () => pickTier(tier.dataset.pickTier));
};

/* ---------- gvb-types: hair-type chips; strands redraw as a sine (amplitude, wavelength) ---------- */
function strand(amp, wave, x0) {
  const pts = [], h = 100, steps = 96;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps, yy = 4 + t * (h - 8);
    const xx = x0 + amp * Math.sin(2 * Math.PI * (yy / wave)) * (0.35 + 0.65 * t);
    pts.push(xx.toFixed(1) + ',' + yy.toFixed(1));
  }
  return 'M' + pts.join(' L');
}
inits.types = (root) => {
  const chips = $('.types-chips', root);
  if (!chips) return;
  const im = $('.type-media img', root);
  tabs(chips, (t) => {
    const d = t.dataset;
    $('[data-type-k]', root).textContent = d.code;
    $('[data-type-fam]', root).textContent = d.fam;
    $('[data-type-d]', root).textContent = d.d;
    if (im && d.img) { im.style.objectPosition = d.pos || ''; swapImg(im, d.img, d.srcset); }
    const amp = Number(d.amp), wave = Number(d.wave);
    $$('[data-strand]', root).forEach((p, i) => { p.setAttribute('d', strand(amp, wave, 14 + i * 24)); });
  });
};

/* ---------- gvb-box: "What's in each set", synced with the buy box ---------- */
inits.box = (root) => {
  const seg = $('[data-box-seg]', root);
  if (!seg) return;
  const bound = root.hasAttribute('data-bound');
  const show = (vid) => { $$('[data-box]', root).forEach((p) => { p.hidden = p.dataset.box !== String(vid); }); };
  const sel = G.radiogroup(seg, (r) => {
    show(r.dataset.v);
    if (bound) selectVariant(root, r.dataset.v, r.dataset.optionIds);
  });
  if (bound) onVariant(root, (v) => {
    const r = $('[data-v="' + v.id + '"]', seg);
    if (r && r.getAttribute('aria-checked') !== 'true') { sel(r, true); show(v.id); }
  });
  $$('[data-box-atc]', root).forEach((b) => b.addEventListener('click', () => {
    const p = b.closest('[data-box]');
    addVariant(b, p.dataset.box, { name: root.dataset.name, variant: p.dataset.variant, price: Number(p.dataset.price), thumb: p.dataset.thumb, source: b });
  }));
};

/* ---------- gvb-shades: find your shade; "Choose X" selects it in the buy box ---------- */
inits.shades = (root) => {
  const bubs = $('.shades-bubbles', root);
  if (!bubs) return;
  const bound = root.hasAttribute('data-bound');
  const L = $('[data-sc-lips]', root), C = $('[data-sc-card]', root), pickBtn = $('[data-sc-pick]', root);
  const pick = tabs(bubs, (t) => {
    const d = t.dataset;
    $('[data-sc-name]', root).textContent = d.name;
    $('[data-sc-fam]', root).textContent = d.fam || '';
    $('[data-sc-fam]', root).hidden = !d.fam;
    $('[data-sc-d]', root).textContent = d.d || '';
    if (L) { L.alt = d.name + ' on lips'; swapImg(L, d.lips, d.lipsSrcset); }
    if (C) { C.alt = d.name + ' swatch'; swapImg(C, d.card, d.cardSrcset); }
    const out = t.hasAttribute('data-soldout');
    $('[data-sc-pick-label]', root).textContent = out ? d.name + ' is sold out' : 'Choose ' + d.name;
    pickBtn.disabled = out;
    pickBtn.classList.toggle('is-soldout', out);
  });
  pickBtn.addEventListener('click', () => {
    const t = $('[role="tab"][aria-selected="true"]', bubs);
    if (!t || t.hasAttribute('data-soldout')) return;
    if (!bound) { location.href = t.dataset.url; return; }
    selectVariant(root, t.dataset.v, t.dataset.optionIds);
    toBuyBox(root.dataset.productId);
  });
  if (bound) onVariant(root, (v) => {
    const t = $('[data-v="' + v.id + '"]', bubs);
    if (t && t.getAttribute('aria-selected') !== 'true') pick(t);
  });
};

/* ---------- gvb-shade-guide: per-shade add (and pick it in the buy box) ---------- */
inits['shade-guide'] = (root) => {
  const bound = root.hasAttribute('data-bound');
  $$('[data-sg-add]', root).forEach((b) => b.addEventListener('click', () => {
    const li = b.closest('[data-sg]');
    if (bound) selectVariant(root, li.dataset.v, li.dataset.optionIds);
    addVariant(b, li.dataset.v, { name: root.dataset.name, variant: li.dataset.sg, price: Number(li.dataset.price), thumb: li.dataset.thumb, source: b });
  }));
};

/* ---------- gvb-tones: tone matrix + tone card; tells the face map which pans to paint ---------- */
inits.tones = (root) => {
  const cols = $('.tmx-cols', root);
  if (!cols) return;
  const bound = root.hasAttribute('data-bound');
  const data = (() => { try { return JSON.parse($('script[data-tones]', root).textContent); } catch (e) { return {}; } })();
  const atc = $('[data-tc-atc]', root);
  const apply = (tone, emit) => {
    const t = data[tone];
    if (!t) return;
    $$('.tmx-col', root).forEach((c) => c.classList.toggle('is-on', c.dataset.col === tone));
    $('[data-tc-name]', root).textContent = tone;
    $('[data-tc-d]', root).textContent = t.d || '';
    $('[data-tc-label]', root).textContent = t.available ? 'Add ' + tone + ' to cart' : tone + ' is sold out';
    atc.disabled = !t.available;
    atc.classList.toggle('is-soldout', !t.available);
    atc.dataset.v = t.id;
    G.tone = { tone, pans: t.pans };
    if (emit !== false) document.dispatchEvent(new CustomEvent('gvb:tone-change', { detail: G.tone }));
  };
  const sel = G.radiogroup(cols, (r) => {
    apply(r.dataset.tonePick);
    if (bound) selectVariant(root, data[r.dataset.tonePick].id, data[r.dataset.tonePick].optionIds);
  });
  if (bound) onVariant(root, (v) => {
    const r = $$('[data-tone-pick]', cols).find((x) => String(data[x.dataset.tonePick].id) === String(v.id));
    if (r) { sel(r, true); apply(r.dataset.tonePick); }
  });
  atc.addEventListener('click', () => {
    const tone = $('[data-tone-pick][aria-checked="true"]', cols).dataset.tonePick, t = data[tone];
    addVariant(atc, t.id, { name: root.dataset.name, variant: tone, price: t.price, thumb: t.thumb, source: atc });
  });
  const first = $('[data-tone-pick][aria-checked="true"]', cols);
  if (first) apply(first.dataset.tonePick);
};

/* ---------- gvb-cross-sell: quick add, kit card, free-delivery maths ---------- */
inits['cross-sell'] = (root) => {
  $$('[data-xs-add]', root).forEach((b) => b.addEventListener('click', () => {
    const p = b.closest('[data-gvb-product]');
    const input = $('input[name="id"]', p);
    addVariant(b, input && input.value, { name: p.dataset.name, variant: p.dataset.variant || '', price: Number(p.dataset.price), thumb: p.dataset.thumb, source: p });
  }));
  $$('[data-pick-tier]', root).forEach((b) => b.addEventListener('click', () => pickTier(b.dataset.pickTier)));

  const note = $('[data-free-note]', root);
  if (!note) return;
  const txt = $('[data-free-note-text]', note);
  const st = { price: Number(root.dataset.price) || 0, cart: Number(root.dataset.cart) || 0 };
  const min = Number(root.dataset.minAdd) || 0, noun = root.dataset.noun || 'order';
  const render = () => {
    const th = threshold();
    const inBag = st.cart > 0;
    const base = inBag ? st.cart : st.price;
    const what = inBag ? 'bag' : noun;
    let s;
    if (base >= th) s = 'Your ' + G.money(base) + ' ' + what + ' already gets free delivery.';
    else if (min && base + min >= th) s = 'Add any one to your ' + G.money(base) + ' ' + what + ' and delivery is free.';
    else s = 'Add ' + G.money(th - base) + ' more to your ' + what + ' for free delivery.';
    txt.textContent = s;
  };
  const pid = root.dataset.productId;
  document.addEventListener('variant:change', (e) => {
    const v = e.detail && e.detail.variant, fid = mainFormId(pid);
    if (!v || (fid && formIdOf(e.target) !== fid)) return;
    st.price = Number(v.price) || st.price; render();
  });
  document.addEventListener('gvb:tier-change', (e) => { if (e.detail && e.detail.price != null) { st.price = Number(e.detail.price); render(); } });
  document.addEventListener('cart:change', (e) => { const c = e.detail && e.detail.cart; if (c && typeof c.total_price === 'number') { st.cart = c.total_price; render(); } });
};

/* ---------- gvb-reviews: filter chips ---------- */
inits.reviews = (root) => {
  const rf = $$('[data-rf]', root);
  rf.forEach((b) => b.addEventListener('click', () => {
    rf.forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
    $$('.rev', root).forEach((r) => r.classList.toggle('is-out', b.dataset.rf !== 'all' && (' ' + r.dataset.fam + ' ').indexOf(' ' + b.dataset.rf + ' ') < 0));
    const rail = $('.revs-rail', root);
    if (rail) { rail.scrollLeft = 0; G.updateRails(root); }
  }));
};

/* ---------- boot ---------- */
function boot(scope) {
  $$('[data-gvb-sec]', scope).forEach((root) => {
    if (root._gvbSec) return;
    root._gvbSec = true;
    const fn = inits[root.dataset.gvbSec];
    if (fn) {
      try { fn(root); } catch (e) { console.error('gvb-sections', root.dataset.gvbSec, e); }
    }
    G.updateRails(root);
  });
}
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));

G.sections = { selectVariant, pickTier, toBuyBox, addVariant, boot };
